import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, TextField, InputAdornment,
  Alert, CircularProgress, Divider, IconButton, Tooltip,
  FormControl, InputLabel, Select, MenuItem, Paper, Grid, Chip, Collapse
} from '@mui/material';
import {
  Send, Save, Add, Delete, AttachMoney,
  Assignment, Build, ShoppingCart, Engineering, Lock, Info,
  ListAlt, ExpandMore, ExpandLess, Warning
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import escrowService from '../../services/escrowService';
import projectService from '../../services/projectService';
import simpleVerificationService from '../../services/simpleVerificationService';
import paymentDraftService from '../../services/paymentDraftService';
import DialogCloseButton from '../common/DialogCloseButton';
import { brand } from '../../theme/colors';

const PAYMENT_TYPES = [
  { value: 'milestone', label: 'Milestone', icon: <Assignment fontSize="small" /> },
  { value: 'labor', label: 'Labor', icon: <Build fontSize="small" /> },
  { value: 'materials', label: 'Materials', icon: <ShoppingCart fontSize="small" /> },
  { value: 'services', label: 'Services', icon: <Engineering fontSize="small" /> },
  { value: 'other', label: 'Other', icon: <AttachMoney fontSize="small" /> },
];

const TYPE_HINTS = {
  milestone: {
    detailsPlaceholder: 'e.g., Foundation completion, roof structure complete',
    itemExamples: ['Foundation work', 'Wall construction', 'Roof installation', 'Finishing'],
  },
  labor: {
    detailsPlaceholder: 'e.g., Roofing crew for 5 days, plumbing rough-in',
    itemExamples: ['Roofing', 'Plumbing', 'Electrical', 'Site clearing', 'Plastering', 'Painting', 'Tiling'],
  },
  materials: {
    detailsPlaceholder: 'e.g., 50 bags cement, 200 bricks, 10 sheets roofing',
    itemExamples: ['Cement (50kg bags)', 'Bricks', 'River sand (tonnes)', 'Roofing sheets', 'Timber', 'Paint (20L)', 'Nails (kg)', 'PVC pipes'],
  },
  services: {
    detailsPlaceholder: 'e.g., Architectural drawings, soil testing, surveying',
    itemExamples: ['Architectural design', 'Structural engineering', 'Soil testing', 'Land surveying', 'Project management', 'Equipment rental'],
  },
  other: {
    detailsPlaceholder: 'Describe what this payment covers',
    itemExamples: ['Item'],
  },
};

const emptyLineItem = () => ({
  id: Date.now(),
  type: 'milestone',
  amount: '',
  recipient: '',
  customRecipient: '',
  details: '',
  breakdownItems: [],
  showBreakdown: false
});

/**
 * NewPaymentForm — single-page payment creation dialog
 */
const NewPaymentForm = ({ open, onClose, onComplete, resumeDraftId = null }) => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);

  const [projectId, setProjectId] = useState('');
  const [lineItems, setLineItems] = useState([emptyLineItem()]);
  const [description, setDescription] = useState('');
  const currency = 'USD';

  const [projects, setProjects] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [projectRecipients, setProjectRecipients] = useState([]);

  // ── Load projects & verifications on open ──
  useEffect(() => {
    if (open && user?.id) {
      loadProjects();
      loadVerifications();
    }
  }, [open, user?.id]);

  useEffect(() => {
    if (!open || !projectId || projects.length === 0) return;
    loadProjectRecipients(projectId);
  }, [open, projectId, projects]);

  // ── Resume draft ──
  useEffect(() => {
    if (!open || !user?.id || !resumeDraftId) {
      setDraftLoading(false);
      return;
    }
    let cancelled = false;
    const fetchDraft = async () => {
      setDraftLoading(true);
      try {
        const draft = await paymentDraftService.getDraft(user.id, resumeDraftId);
        if (cancelled) return;
        if (draft) {
          if (draft.paymentData) {
            setProjectId(draft.paymentData.projectId || '');
            setDescription(draft.paymentData.description || '');
          }
          if (Array.isArray(draft.lineItems) && draft.lineItems.length > 0) {
            setLineItems(draft.lineItems.map(item => ({
              id: item.id || Date.now() + Math.random(),
              type: item.paymentType || item.type || 'milestone',
              amount: item.amount || '',
              recipient: item.recipient || '',
              customRecipient: item.customRecipient || '',
              details: item.details || '',
              breakdownItems: Array.isArray(item.breakdownItems) ? item.breakdownItems : [],
              showBreakdown: Array.isArray(item.breakdownItems) && item.breakdownItems.length > 0
            })));
          }
          setDraftId(draft.id);
          setSuccess('Draft loaded. Review and submit when ready.');
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Unable to load draft.');
      } finally {
        if (!cancelled) setDraftLoading(false);
      }
    };
    fetchDraft();
    return () => { cancelled = true; };
  }, [open, user?.id, resumeDraftId]);

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects(user.id);
      const list = Array.isArray(response?.data?.projects)
        ? response.data.projects
        : Array.isArray(response?.projects) ? response.projects
        : Array.isArray(response) ? response : [];
      setProjects(list);
    } catch (err) { console.error('Failed to load projects:', err); }
  };

  const loadVerifications = async () => {
    try {
      const response = await simpleVerificationService.getMyVerifications('all');
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response) ? response : [];
      setVerifications(list);
    } catch (err) { console.error('Failed to load verifications:', err); }
  };

  const loadProjectRecipients = async (selectedId) => {
    try {
      setProjectRecipients([]);
      if (!selectedId.startsWith('project-')) return;
      const rawId = selectedId.replace('project-', '');
      const project = projects.find(p => String(p.id) === String(rawId));
      if (!project) return;
      const recipients = [];
      if (project.contractorId) {
        recipients.push({ id: project.contractorId, name: project.contractorName || 'Project Contractor', type: 'contractor' });
      }
      verifications.filter(v => String(v.projectId) === String(rawId)).forEach(v => {
        if (v.verifierId) recipients.push({ id: v.verifierId, name: v.verifierName || 'Verifier', type: 'verifier' });
      });
      setProjectRecipients(recipients);
    } catch (err) { setProjectRecipients([]); }
  };

  // ── Line item helpers ──
  const addLineItem = () => setLineItems([...lineItems, emptyLineItem()]);

  const removeLineItem = (id) => {
    if (lineItems.length > 1) setLineItems(lineItems.filter(i => i.id !== id));
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(lineItems.map(i => (i.id === id ? { ...i, [field]: value } : i)));
  };

  // ── Breakdown sub-item helpers ──
  const addBreakdownItem = (lineItemId) => {
    setLineItems(lineItems.map(i => {
      if (i.id !== lineItemId) return i;
      return {
        ...i,
        breakdownItems: [...i.breakdownItems, { name: '', quantity: '', unitPrice: '' }]
      };
    }));
  };

  const updateBreakdownItem = (lineItemId, idx, field, value) => {
    setLineItems(lineItems.map(i => {
      if (i.id !== lineItemId) return i;
      const items = [...i.breakdownItems];
      items[idx] = { ...items[idx], [field]: value };
      return { ...i, breakdownItems: items };
    }));
  };

  const removeBreakdownItem = (lineItemId, idx) => {
    setLineItems(lineItems.map(i => {
      if (i.id !== lineItemId) return i;
      return { ...i, breakdownItems: i.breakdownItems.filter((_, j) => j !== idx) };
    }));
  };

  const getBreakdownSum = (items) =>
    items.reduce((s, si) => s + (parseFloat(si.quantity) || 0) * (parseFloat(si.unitPrice) || 0), 0);

  // ── Calculations ──
  const subtotal = lineItems.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
  const platformFee = subtotal * 0.025;
  const total = subtotal + platformFee;

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

  // ── Resolve project / verification IDs ──
  const resolveSelectionIds = useCallback(() => {
    if (!projectId) return { projectId: null, verificationId: null };
    const [prefix, rawId] = projectId.split('-');
    if (!rawId) return { projectId: null, verificationId: null };
    if (prefix === 'project') return { projectId: rawId, verificationId: null };
    if (prefix === 'verification') {
      const matched = verifications.find(v => String(v.id) === String(rawId));
      const numericId = Number(rawId);
      return { projectId: matched?.projectId ?? null, verificationId: Number.isNaN(numericId) ? null : numericId };
    }
    return { projectId: null, verificationId: null };
  }, [projectId, verifications]);

  // ── Validation ──
  const validate = () => {
    if (!projectId) return 'Please select a project or verification.';
    if (lineItems.length === 0) return 'Add at least one payment item.';
    for (const item of lineItems) {
      if (!item.amount || parseFloat(item.amount) <= 0) return 'Every item must have a valid amount.';
      const hasRecipient = item.recipient && item.recipient !== '';
      const hasCustom = item.recipient === 'other' && item.customRecipient?.trim();
      if (!hasRecipient || (item.recipient === 'other' && !hasCustom)) return 'Every item must have a recipient.';
    }
    return null;
  };

  // ── Build breakdown payload for a line item ──
  const buildBreakdown = (item) => {
    const filteredItems = (item.breakdownItems || [])
      .filter(si => si.name?.trim())
      .map(si => ({
        name: si.name.trim(),
        quantity: parseFloat(si.quantity) || 0,
        unitPrice: parseFloat(si.unitPrice) || 0
      }));
    if (!item.details && filteredItems.length === 0) return null;
    return { details: item.details || '', items: filteredItems };
  };

  // ── Create payment ──
  const handleCreate = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    setError('');

    try {
      const { projectId: resolvedProjectId, verificationId } = resolveSelectionIds();
      const selectedProject = resolvedProjectId
        ? projects.find(p => String(p.id) === String(resolvedProjectId)) : null;
      const selectedVerification = verificationId
        ? verifications.find(v => String(v.id) === String(verificationId)) : null;
      const finalProjectId = resolvedProjectId || selectedVerification?.projectId;

      const milestones = lineItems.map(item => {
        const recipientName = item.recipient === 'other'
          ? (item.customRecipient || 'Unknown') : item.recipient;
        return {
          description: `${item.type.charAt(0).toUpperCase() + item.type.slice(1)} payment to ${recipientName}`,
          amount: parseFloat(item.amount),
          payment_type: item.type,
          labor_amount: item.type === 'labor' ? parseFloat(item.amount) : 0,
          materials_amount: item.type === 'materials' ? parseFloat(item.amount) : 0,
          recipient: recipientName,
          breakdown: buildBreakdown(item)
        };
      });

      const escrowData = {
        projectId: finalProjectId,
        verificationId,
        contractorUserId: selectedProject?.contractor_id || selectedVerification?.verifierId || user.id,
        totalAmount: subtotal,
        currency,
        milestones,
        fundingSource: 'manual',
        purpose: description || lineItems[0]?.type || 'payment'
      };

      const result = await escrowService.createEscrowAccount(escrowData);

      if (draftId) {
        paymentDraftService.deleteDraft(user.id, draftId).catch(() => {});
        setDraftId(null);
      }

      setSuccess('Payment created successfully!');
      setTimeout(() => {
        if (onComplete) onComplete(result);
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to create payment.');
    } finally {
      setLoading(false);
    }
  };

  // ── Save draft ──
  const handleSaveDraft = async () => {
    if (!user?.id) { setError('Sign in required.'); return; }
    setDraftSaving(true);
    setError('');
    try {
      const { projectId: resolvedProjectId, verificationId } = resolveSelectionIds();
      const payload = {
        id: draftId,
        projectId: resolvedProjectId,
        verificationId,
        paymentData: { projectId, description, currency },
        lineItems: lineItems.map(i => ({
          id: i.id,
          paymentType: i.type,
          amount: i.amount,
          recipient: i.recipient,
          customRecipient: i.customRecipient,
          details: i.details,
          breakdownItems: i.breakdownItems
        })),
        status: 'draft'
      };
      const saved = await paymentDraftService.saveDraft(user.id, payload);
      setDraftId(saved?.id || draftId);
      setSuccess('Draft saved.');
      setTimeout(() => handleClose(), 600);
    } catch (err) {
      setError(err?.message || 'Failed to save draft.');
    } finally {
      setDraftSaving(false);
    }
  };

  // ── Close / reset ──
  const handleClose = () => {
    setProjectId('');
    setLineItems([emptyLineItem()]);
    setDescription('');
    setError('');
    setSuccess('');
    setDraftId(null);
    setDraftSaving(false);
    setDraftLoading(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogCloseButton onClick={handleClose} />
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
        <Send sx={{ color: brand.primary }} />
        New Payment
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5 }}>
        {draftLoading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>Loading draft...</Typography>
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
            )}

            {/* ── Project selector ── */}
            <FormControl fullWidth required sx={{ mb: 3 }}>
              <InputLabel>Project / Verification</InputLabel>
              <Select
                value={projectId}
                label="Project / Verification"
                onChange={(e) => setProjectId(e.target.value)}
              >
                <MenuItem value="" disabled><em>Select a project or verification</em></MenuItem>
                {projects.length > 0 && (
                  <MenuItem disabled sx={{ fontWeight: 'bold', color: brand.primary, fontSize: 13 }}>PROJECTS</MenuItem>
                )}
                {projects.map(p => (
                  <MenuItem key={`project-${p.id}`} value={`project-${p.id}`}>
                    {p.title}{p.budget ? ` — $${p.budget}` : ''}
                  </MenuItem>
                ))}
                {verifications.length > 0 && (
                  <MenuItem disabled sx={{ fontWeight: 'bold', color: brand.primary, fontSize: 13, mt: 0.5 }}>VERIFICATIONS</MenuItem>
                )}
                {verifications.map(v => (
                  <MenuItem key={`verification-${v.id}`} value={`verification-${v.id}`}>
                    {v.title}{v.cost ? ` — $${v.cost}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* ── Payment items ── */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Payment Items
            </Typography>

            {lineItems.map((item) => {
              const breakdownSum = getBreakdownSum(item.breakdownItems);
              const itemAmount = parseFloat(item.amount) || 0;
              const hasMismatch = item.breakdownItems.length > 0 && breakdownSum > 0 && itemAmount > 0
                && Math.abs(breakdownSum - itemAmount) > 0.01;

              return (
                <Paper key={item.id} variant="outlined" sx={{ p: 2, mb: 1.5, bgcolor: '#fafbfc' }}>
                  {/* Main row: Type / Amount / Recipient / Delete */}
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={item.type}
                          label="Type"
                          onChange={(e) => updateLineItem(item.id, 'type', e.target.value)}
                        >
                          {PAYMENT_TYPES.map(pt => (
                            <MenuItem key={pt.value} value={pt.value}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {pt.icon}<span>{pt.label}</span>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth size="small" label="Amount" type="number"
                        value={item.amount}
                        onChange={(e) => updateLineItem(item.id, 'amount', e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink>Recipient</InputLabel>
                        <Select
                          value={item.recipient}
                          label="Recipient"
                          displayEmpty
                          notched
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <Typography component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                  {!projectId ? 'Select project first' : 'Select recipient'}
                                </Typography>
                              );
                            }
                            if (selected === 'other') return 'Other / Manual';
                            return selected;
                          }}
                          onChange={(e) => updateLineItem(item.id, 'recipient', e.target.value)}
                          sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', minHeight: 24 } }}
                        >
                          <MenuItem value="" disabled>
                            <em>{!projectId ? 'Select project first' : 'Select recipient'}</em>
                          </MenuItem>
                          {projectRecipients.map(r => (
                            <MenuItem key={r.id} value={r.name}>
                              {r.type === 'contractor' ? '👷 ' : '✓ '}{r.name}
                            </MenuItem>
                          ))}
                          <MenuItem value="other">Other / Manual</MenuItem>
                        </Select>
                      </FormControl>
                      {item.recipient === 'other' && (
                        <TextField
                          fullWidth size="small" placeholder="Enter recipient name"
                          value={item.customRecipient || ''}
                          onChange={(e) => updateLineItem(item.id, 'customRecipient', e.target.value)}
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={1} sx={{ textAlign: 'center' }}>
                      <IconButton size="small" color="error" onClick={() => removeLineItem(item.id)} disabled={lineItems.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>

                  {/* Details text field (always visible) */}
                  <TextField
                    fullWidth size="small"
                    placeholder={TYPE_HINTS[item.type]?.detailsPlaceholder || 'Describe what this payment covers'}
                    value={item.details || ''}
                    onChange={(e) => updateLineItem(item.id, 'details', e.target.value)}
                    sx={{ mt: 1.5 }}
                  />

                  {/* Itemized breakdown toggle */}
                  <Button
                    size="small"
                    startIcon={item.showBreakdown ? <ExpandLess /> : <ListAlt />}
                    onClick={() => updateLineItem(item.id, 'showBreakdown', !item.showBreakdown)}
                    sx={{ mt: 1, color: brand.primary, textTransform: 'none', fontSize: 12 }}
                  >
                    {item.showBreakdown ? 'Hide breakdown' : 'Add itemized breakdown'}
                  </Button>

                  <Collapse in={item.showBreakdown}>
                    <Box sx={{ mt: 1, pl: 1, pr: 1 }}>
                      {/* Sub-item header */}
                      <Grid container spacing={1} sx={{ mb: 0.5 }}>
                        <Grid item xs={5}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>Item</Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>Qty</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>Unit Price</Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>Total</Typography>
                        </Grid>
                      </Grid>

                      {/* Sub-item rows */}
                      {item.breakdownItems.map((si, idx) => {
                        const siTotal = (parseFloat(si.quantity) || 0) * (parseFloat(si.unitPrice) || 0);
                        return (
                          <Grid container spacing={1} key={idx} alignItems="center" sx={{ mb: 0.5 }}>
                            <Grid item xs={5}>
                              <TextField
                                fullWidth size="small"
                                placeholder={`e.g., ${(TYPE_HINTS[item.type]?.itemExamples || ['Item'])[idx % (TYPE_HINTS[item.type]?.itemExamples || ['Item']).length]}`}
                                value={si.name}
                                onChange={(e) => updateBreakdownItem(item.id, idx, 'name', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <TextField
                                fullWidth size="small" type="number" placeholder="0"
                                value={si.quantity}
                                onChange={(e) => updateBreakdownItem(item.id, idx, 'quantity', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                fullWidth size="small" type="number" placeholder="0.00"
                                value={si.unitPrice}
                                onChange={(e) => updateBreakdownItem(item.id, idx, 'unitPrice', e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                              />
                            </Grid>
                            <Grid item xs={1}>
                              <Typography variant="body2" sx={{ fontSize: 12, textAlign: 'right' }}>
                                ${siTotal.toFixed(0)}
                              </Typography>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'center' }}>
                              <IconButton size="small" onClick={() => removeBreakdownItem(item.id, idx)} sx={{ p: 0.25 }}>
                                <Delete sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Grid>
                          </Grid>
                        );
                      })}

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                        <Button
                          size="small" startIcon={<Add />}
                          onClick={() => addBreakdownItem(item.id)}
                          sx={{ color: brand.primary, textTransform: 'none', fontSize: 12 }}
                        >
                          Add item
                        </Button>
                        {item.breakdownItems.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {hasMismatch && (
                              <Chip
                                icon={<Warning sx={{ fontSize: 14 }} />}
                                label="Sum differs from amount"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ fontSize: 11, height: 22 }}
                              />
                            )}
                            <Typography variant="caption" fontWeight={600}>
                              Sum: ${breakdownSum.toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}

            <Button
              size="small" startIcon={<Add />} onClick={addLineItem}
              sx={{ mb: 2.5, color: brand.primary }}
            >
              Add another recipient
            </Button>

            {/* ── Description ── */}
            <TextField
              fullWidth label="Description (optional)" multiline rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Additional details about this payment"
              sx={{ mb: 3 }}
            />

            {/* ── Live fee summary ── */}
            {subtotal > 0 && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0f4ff', borderColor: '#bfdbfe' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">Subtotal</Typography>
                  <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">Platform Fee (2.5%)</Typography>
                    <Tooltip title="Covers escrow protection, verification, and secure payment processing">
                      <Info sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="body2">{formatCurrency(platformFee)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: brand.primary }}>
                    {formatCurrency(total)}
                  </Typography>
                </Box>
                <Alert
                  severity="success" icon={<Lock fontSize="small" />}
                  sx={{ mt: 1.5, py: 0, '& .MuiAlert-message': { fontSize: 12 } }}
                >
                  Held in wallet until work is verified
                </Alert>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={handleSaveDraft}
          disabled={draftSaving || loading || draftLoading}
          startIcon={draftSaving ? <CircularProgress size={18} /> : <Save />}
        >
          Save Draft
        </Button>
        <Button
          variant="contained" onClick={handleCreate}
          disabled={loading || subtotal === 0 || draftLoading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Send />}
          sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
        >
          Create Payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewPaymentForm;
